import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import * as validators from '../../../util/validators';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';

import { Form, PrimaryButton, FieldTextInput, CustomExtendedDataField } from '../../../components';

import FieldSelectUserType from '../FieldSelectUserType';
import UserFieldDisplayName from '../UserFieldDisplayName';
import UserFieldPhoneNumber from '../UserFieldPhoneNumber';

import css from './SignupForm.module.css';
import Icons from '../../../components/Icons/Icons';
import { getUserTypeDescription } from '../../../util/genericHelpers';

const getSoleUserTypeMaybe = userTypes =>
  Array.isArray(userTypes) && userTypes.length === 1 ? userTypes[0].userType : null;

const isPasswordUsedMoreThanOnce = formValues => {
  const pw = formValues.password;
  const hasPasswordString = pw != null && pw.length >= validators.PASSWORD_MIN_LENGTH;

  if (hasPasswordString) {
    const isPasswordRepeated = Object.values(formValues).filter(v => v === pw).length > 1;
    return isPasswordRepeated;
  }
  return false;
};

const createFormValidation = intl => values => {
  const errors = {};
  if (values.confirmPassword !== values.password) {
    if (values.confirmPassword) {
      errors.confirmPassword = intl.formatMessage({
        id: 'SignupForm.passwordsDoNotMatch',
      });
    }
  }

  return errors;
};

const SignupFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    initialValues={{ userType: props.preselectedUserType || getSoleUserTypeMaybe(props.userTypes) }}
    validate={createFormValidation(props.intl)}
    render={formRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        termsAndConditions,
        userTypes,
        userFields,
        values,
        form,
      } = formRenderProps;

      const { userType } = values || {};

      // Email
      const emailRequired = validators.required(
        intl.formatMessage({ id: 'SignupForm.emailRequired' })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({ id: 'SignupForm.emailInvalid' })
      );

      // Password
      const passwordRequiredMessage = intl.formatMessage({ id: 'SignupForm.passwordRequired' });
      const passwordMinLengthMessage = intl.formatMessage(
        { id: 'SignupForm.passwordTooShort' },
        { minLength: validators.PASSWORD_MIN_LENGTH }
      );
      const passwordMaxLengthMessage = intl.formatMessage(
        { id: 'SignupForm.passwordTooLong' },
        { maxLength: validators.PASSWORD_MAX_LENGTH }
      );

      const passwordValidators = validators.composeValidators(
        validators.requiredStringNoTrim(passwordRequiredMessage),
        validators.minLength(passwordMinLengthMessage, validators.PASSWORD_MIN_LENGTH),
        validators.maxLength(passwordMaxLengthMessage, validators.PASSWORD_MAX_LENGTH)
      );

      // Confirm password
      const confirmPasswordRequired = validators.requiredStringNoTrim(
        intl.formatMessage({ id: 'SignupForm.confirmPasswordRequired' })
      );

      // Custom user fields
      const userFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);

      const noUserTypes = !userType && !(userTypes?.length > 0);
      const userTypeConfig = userTypes?.find(config => config.userType === userType);
      const showDefaultUserFields = userType || noUserTypes;
      const showCustomUserFields = (userType || noUserTypes) && userFieldProps?.length > 0;

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress;

      // Password visibility
      const [showPassword, setShowPassword] = useState(false);
      const [showConfirmPassword, setShowConfirmPassword] = useState(false);

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <FieldSelectUserType name="userType" userTypes={userTypes} intl={intl} />

          <div className={css.userTypeDescription}>
            {' '}
            {userType && getUserTypeDescription(userType)}
          </div>

          {showDefaultUserFields && (
            <div className={css.defaultUserFields}>
              <FieldTextInput
                className={css.formField}
                type="email"
                id={formId ? `${formId}.email` : 'email'}
                name="email"
                autoComplete="email"
                label={intl.formatMessage({ id: 'SignupForm.emailLabel' })}
                placeholder={intl.formatMessage({ id: 'SignupForm.emailPlaceholder' })}
                validate={validators.composeValidators(emailRequired, emailValid)}
              />

              <div className={css.name}>
                <FieldTextInput
                  className={classNames(css.formField, css.firstNameRoot)}
                  type="text"
                  id={formId ? `${formId}.fname` : 'fname'}
                  name="fname"
                  autoComplete="given-name"
                  label={intl.formatMessage({ id: 'SignupForm.firstNameLabel' })}
                  placeholder={intl.formatMessage({ id: 'SignupForm.firstNamePlaceholder' })}
                  validate={validators.required(
                    intl.formatMessage({ id: 'SignupForm.firstNameRequired' })
                  )}
                />
                <FieldTextInput
                  className={classNames(css.formField, css.lastNameRoot)}
                  type="text"
                  id={formId ? `${formId}.lname` : 'lname'}
                  name="lname"
                  autoComplete="family-name"
                  label={intl.formatMessage({ id: 'SignupForm.lastNameLabel' })}
                  placeholder={intl.formatMessage({ id: 'SignupForm.lastNamePlaceholder' })}
                  validate={validators.required(
                    intl.formatMessage({ id: 'SignupForm.lastNameRequired' })
                  )}
                />
              </div>

              <UserFieldDisplayName
                formName="SignupForm"
                className={css.row}
                userTypeConfig={userTypeConfig}
                intl={intl}
              />

              <div className={css.passwordWrapper}>
                <FieldTextInput
                  className={classNames(css.formField, css.password)}
                  type={showPassword ? 'text' : 'password'}
                  id={formId ? `${formId}.password` : 'password'}
                  name="password"
                  autoComplete="new-password"
                  label={intl.formatMessage({ id: 'SignupForm.passwordLabel' })}
                  placeholder={intl.formatMessage({ id: 'SignupForm.passwordPlaceholder' })}
                  validate={passwordValidators}
                />
                <button
                  type="button"
                  className={css.togglePassword}
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <Icons name="eyeOpen" /> : <Icons name="eyeClose" />}
                </button>
              </div>

              <div className={css.passwordWrapper}>
                <FieldTextInput
                  className={classNames(css.formField, css.password)}
                  type={showConfirmPassword ? 'text' : 'password'}
                  id={formId ? `${formId}.confirmPassword` : 'confirmPassword'}
                  name="confirmPassword"
                  autoComplete="new-password"
                  label={intl.formatMessage({ id: 'SignupForm.confirmPasswordLabel' })}
                  placeholder={intl.formatMessage({ id: 'SignupForm.confirmPasswordPlaceholder' })}
                  validate={confirmPasswordRequired}
                />
                <button
                  type="button"
                  className={css.toggleConfirmPassword}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <Icons name="eyeOpen" /> : <Icons name="eyeClose" />}
                </button>
              </div>

              <UserFieldPhoneNumber
                formName="SignupForm"
                className={css.row}
                userTypeConfig={userTypeConfig}
                intl={intl}
              />
            </div>
          )}

          {showCustomUserFields && (
            <div className={css.customFields}>
              {userFieldProps.map(({ key, ...fieldProps }) => (
                <CustomExtendedDataField key={key} {...fieldProps} formId={formId} />
              ))}
            </div>
          )}

          <div className={css.bottomWrapper}>
            {termsAndConditions}
            <PrimaryButton
              rootClassName={css.submitButton}
              type="submit"
              inProgress={submitInProgress}
              disabled={submitDisabled}
            >
              <FormattedMessage id="SignupForm.signUp" />
            </PrimaryButton>
            <div className={css.trustMessage}>
              <p className={css.trustMessageText}>
                {' '}
                {intl.formatMessage({ id: 'SignupForm.trustMessage' })}
              </p>
            </div>
          </div>
        </Form>
      );
    }}
  />
);

/**
 * A component that renders the signup form.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName - The root class name that overrides the default class css.root
 * @param {string} props.className - The class that extends the root class
 * @param {string} props.formId - The form id
 * @param {boolean} props.inProgress - Whether the form is in progress
 * @param {ReactNode} props.termsAndConditions - The terms and conditions
 * @param {string} props.preselectedUserType - The preselected user type
 * @param {propTypes.userTypes} props.userTypes - The user types
 * @param {propTypes.listingFields} props.userFields - The user fields
 * @returns {JSX.Element}
 */
const SignupForm = props => {
  const intl = useIntl();
  return <SignupFormComponent {...props} intl={intl} />;
};

export default SignupForm;
